import { useState, useEffect } from "react";
import {
  useParams,
  useRouteContext,
  getRouteApi,
} from "@tanstack/react-router";
import { FiLock } from "react-icons/fi";
import Card from "@/components/shared/Card";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import { fetchAuthenticated } from "@/queries/shared";
import { useMonthlyOnDemandReports } from "@/queries";
import ReportFrame from "@/components/shared/ReportFrame";
import {
  buildMonthStartFilter,
  isMonthlyAttendanceReport,
  isSupplementalReport,
} from "@/utils/reportFilters";

const rootRoute = getRouteApi("__root__");

type ReportTypeOption = "monthly" | "supplemental";

const REPORT_TYPE_LABELS: Record<ReportTypeOption, string> = {
  monthly: "Monthly Certification Report",
  supplemental: "Supplemental Report",
};

// TODO: add reportType value(s) for Fall and EOY when the API introduces them.
const REPORT_TYPE_PERMISSION_MAP: Record<string, string[]> = {
  ReportsMonthly: ["OnDemand"],
  ReportsFall: [],
  ReportsEOY: [],
};

const ROLE_LABELS: Record<string, string> = {
  ReportsMonthly: "Monthly Reports",
  ReportsFall: "Fall Reports",
  ReportsEOY: "EOY Reports",
};

const isMonthlyIEPStatusReport = (name: string): boolean =>
  name.toLowerCase().includes("iep/ifsp");

const requiresMonthParameter = (report: {
  name: string;
  serverURL: string;
}): boolean =>
  isMonthlyIEPStatusReport(report.name) ||
  isMonthlyAttendanceReport(report.serverURL);

const getMonthStartLabel = (monthStart: string): string => {
  const [year, month] = monthStart.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const getPreviousMonthStart = (monthStart: string): string => {
  const [year, month] = monthStart.split("-").map(Number);
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  return `${prevYear}-${String(prevMonth).padStart(2, "0")}-01`;
};

const MONTHLY_IEP_STATUS_MIN_MONTH_START = "2026-07-01";

const getMonthlyIEPStatusMonthOptions = (
  includeCurrentMonth: boolean
): {
  value: string;
  label: string;
}[] => {
  const now = new Date();
  const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  let cursor = includeCurrentMonth
    ? currentMonthStart
    : getPreviousMonthStart(currentMonthStart);
  const options: { value: string; label: string }[] = [];

  while (cursor >= MONTHLY_IEP_STATUS_MIN_MONTH_START) {
    options.push({ value: cursor, label: getMonthStartLabel(cursor) });
    cursor = getPreviousMonthStart(cursor);
  }

  return options;
};

const buildMonthFilterClause = (monthStart: string): string =>
  `+and+${buildMonthStartFilter(monthStart)}`;

const buildReportUrl = (serverURL: string, monthFilterClause = ""): string => {
  const allowedPrefix = "reports/powerbi/";
  if (!serverURL.startsWith(allowedPrefix)) {
    console.error("Unexpected report URL:", serverURL);
    return "";
  }
  return `/${serverURL}${monthFilterClause}`;
};

const ReportsContent = ({
  leaid,
  allowedReportTypes,
}: {
  leaid: string;
  allowedReportTypes: string[];
}) => {
  const [reportType, setReportType] = useState<ReportTypeOption>("monthly");
  const [selectedReport, setSelectedReport] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [viewedReport, setViewedReport] = useState<string>("");
  const [viewedMonthFilter, setViewedMonthFilter] = useState<string>("");
  const [cookieReady, setCookieReady] = useState(false);
  const [cookieError, setCookieError] = useState(false);
  const globalState = rootRoute.useLoaderData();
  const { hasFeature } = useRouteContext({ from: "/$leaid" });

  const { data: availableReports = [] } = useMonthlyOnDemandReports(leaid);
  const permittedReports = availableReports.filter(
    (report) =>
      allowedReportTypes.includes(report.reportType) &&
      (hasFeature("Attendance") || !isMonthlyAttendanceReport(report.serverURL))
  );
  const reportsForType = permittedReports
    .filter((report) =>
      reportType === "supplemental"
        ? isSupplementalReport(report)
        : !isSupplementalReport(report)
    )
    .map((report, index) => ({
      ...report,
      label: `${reportType === "supplemental" ? "5" : "1"}.${index + 1} – ${report.name}`,
    }));

  const selectedReportInfo = reportsForType.find(
    (report) => report.serverURL === selectedReport
  );
  const viewedReportInfo = reportsForType.find(
    (report) => report.serverURL === viewedReport
  );
  const isMonthParameterReportSelected =
    !!selectedReportInfo &&
    requiresMonthParameter(selectedReportInfo);
  const monthOptions = getMonthlyIEPStatusMonthOptions(
    !selectedReportInfo ||
      !isMonthlyAttendanceReport(selectedReportInfo.serverURL)
  );

  const reportUrl = viewedReport
    ? buildReportUrl(viewedReport, viewedMonthFilter)
    : "";

  useEffect(() => {
    let cancelled = false;
    setCookieReady(false);
    setReportType("monthly");
    setSelectedReport("");
    setSelectedMonth("");
    setViewedReport("");
    setViewedMonthFilter("");

    async function initCookie() {
      try {
        setCookieError(false);
        await fetchAuthenticated(
          globalState,
          `/ReportDirectory/${leaid}/getcookie`
        );
        if (!cancelled) {
          setCookieReady(true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to initialize report cookie:", error);
        setCookieError(true);
      }
    }
    initCookie();

    return () => {
      cancelled = true;
    };
  }, [leaid, globalState]);

  return (
    <div className="d-flex flex-column gap-4">
      <Card className="card-custom shadow-none d-flex flex-wrap align-items-end gap-3">
        <div>
          <label
            htmlFor="report-type-select"
            className="form-label fw-semibold d-block"
          >
            Select Report Type
          </label>
          <select
            id="report-type-select"
            value={reportType}
            onChange={(e) => {
              setReportType(e.target.value as ReportTypeOption);
              setSelectedReport("");
              setSelectedMonth("");
              setViewedReport("");
              setViewedMonthFilter("");
            }}
            className="form-select"
            style={{ maxWidth: "350px" }}
          >
            <option value="monthly">{REPORT_TYPE_LABELS.monthly}</option>
            <option value="supplemental">
              {REPORT_TYPE_LABELS.supplemental}
            </option>
          </select>
        </div>

        <div>
          <label
            htmlFor="report-select"
            className="form-label fw-semibold d-block"
          >
            Select a Report
          </label>
          <select
            id="report-select"
            value={selectedReport}
            onChange={(e) => {
              setSelectedReport(e.target.value);
              setSelectedMonth("");
            }}
            className="form-select"
            style={{ maxWidth: "350px" }}
          >
            <option value="">Select a report</option>
            {reportsForType.map((report) => (
              <option key={report.reportId} value={report.serverURL}>
                {report.label}
              </option>
            ))}
          </select>
        </div>

        {isMonthParameterReportSelected && (
          <div>
            <label
              htmlFor="report-month-select"
              className="form-label fw-semibold d-block"
            >
              Select Month
            </label>
            <select
              id="report-month-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="form-select"
              style={{ maxWidth: "350px" }}
            >
              <option value="">Select a month</option>
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary"
          disabled={
            !selectedReport ||
            (isMonthParameterReportSelected && !selectedMonth)
          }
          onClick={() => {
            setViewedReport(selectedReport);
            setViewedMonthFilter(
              isMonthParameterReportSelected && selectedMonth
                ? buildMonthFilterClause(selectedMonth)
                : ""
            );
          }}
          aria-describedby="view-report-keyboard-tip"
        >
          View
        </button>
        <span id="view-report-keyboard-tip" className="visually-hidden">
          Opens the report in an embedded Power BI viewer. Inside the report,
          press Escape to exit a visual, and keep pressing Escape until you
          reach a skip link above the report and continue navigating the page.
        </span>
      </Card>

      {viewedReport && cookieReady && reportUrl && (
        <div
          className="w-100 d-flex flex-column"
          style={{ height: "calc(100vh - 250px)" }}
        >
          <ReportFrame
            src={reportUrl}
            title={viewedReportInfo?.label ?? "Power BI Report"}
          />
        </div>
      )}

      {viewedReport && !cookieReady && !cookieError && (
        <div className="text-secondary">Loading report...</div>
      )}

      {cookieError && (
        <div className="text-danger">
          Failed to load report. Please try again.
        </div>
      )}
    </div>
  );
};

const NoReportsAccess = ({ missingRoles }: { missingRoles: string[] }) => {
  const missingLabels = missingRoles.map((r) => ROLE_LABELS[r] ?? r).join(", ");

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        flexDirection: "column",
        minHeight: 200,
        gap: "0.5rem",
        padding: "3rem 1rem",
        textAlign: "center",
      }}
    >
      <FiLock size={32} style={{ color: "#334155", marginBottom: "0.25rem" }} />
      <span style={{ fontWeight: 600, color: "#334155", fontSize: "1rem" }}>
        You don't have access to {missingLabels}
      </span>
      {/* #475569 on white = 7.0:1 contrast ratio — passes WCAG AA and AAA */}
      <span style={{ color: "#475569", fontSize: "0.95rem" }}>
        Please contact your administrator for access.
      </span>
    </div>
  );
};

const NoReportsAvailable = () => (
  <div
    className="d-flex align-items-center justify-content-center"
    style={{
      flexDirection: "column",
      minHeight: 200,
      gap: "0.5rem",
      padding: "3rem 1rem",
      textAlign: "center",
    }}
  >
    <FiLock size={32} style={{ color: "#334155", marginBottom: "0.25rem" }} />
    <span style={{ fontWeight: 600, color: "#334155", fontSize: "1rem" }}>
      No reports are currently available for your access level
    </span>
    <span style={{ color: "#475569", fontSize: "0.95rem" }}>
      Please check back later or contact your administrator for assistance.
    </span>
  </div>
);

const ReportsPage = () => {
  const { leaid } = useParams({ from: "/$leaid" });
  const { hasRole } = useRouteContext({ from: "/$leaid" });

  const hasAnyReportRole = Object.keys(REPORT_TYPE_PERMISSION_MAP).some(
    (role) => hasRole(role)
  );

  const allowedReportTypes = Object.entries(REPORT_TYPE_PERMISSION_MAP)
    .filter(([role]) => hasRole(role))
    .flatMap(([, types]) => types);

  const hasConfiguredReportTypes = allowedReportTypes.length > 0;

  const missingRoles = Object.entries(REPORT_TYPE_PERMISSION_MAP)
    .filter(([, types]) => types.length > 0)
    .map(([role]) => role)
    .filter((role) => !hasRole(role));

  return (
    <LayoutContentArea headingText="Reports">
      {!hasAnyReportRole ? (
        <NoReportsAccess missingRoles={missingRoles} />
      ) : !hasConfiguredReportTypes ? (
        <NoReportsAvailable />
      ) : (
        <ReportsContent leaid={leaid} allowedReportTypes={allowedReportTypes} />
      )}
    </LayoutContentArea>
  );
};

export default ReportsPage;
